// apps/frontend/src/App.tsx
// 🚀 Application principale ULTRA PREMIUM avec architecture optimisée
// 🎨 COULEURS BEIGE GLOWP + Design System cohérent
// ⚡ PERFORMANCE : Lazy Loading + Code Splitting + Mémoization
// 🔒 SÉCURITÉ : Protected Routes + Auth Guards + Token Validation
// 🌍 I18N : Gestion multilingue parfaite avec persistance
// 🛒 CART SYSTEM : Vinted-like avec état global persistant
// 📱 RESPONSIVE : Mobile-first + Desktop optimisé
// ♿ ACCESSIBILITÉ : ARIA labels + Keyboard navigation

import 'leaflet/dist/leaflet.css';
import React, {
  useState,
  useEffect,
  useRef,
  Suspense,
  useMemo,
  useCallback,
} from 'react';
// Drop-in replacement de React.lazy : auto-reload si chunk obsolète après
// déploiement (cf. ../lib/lazyWithRetry pour le détail).
import { lazy } from './lib/lazyWithRetry';
import { useTranslation } from 'react-i18next';
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorState from './components/common/ErrorState';
import MarketingConsentBanner from './components/common/MarketingConsentBanner';
import AppFeedbackPrompt from './components/feedback/AppFeedbackPrompt';
import SignupBonusWelcome from './components/auth/SignupBonusWelcome';
import ExistingUserFeedbackBanner from './components/feedback/ExistingUserFeedbackBanner';
import { captureError } from './lib/sentry';

import { API_BASE_URL } from './config/api';
import {
  Baby,
  X as XIcon,
  Check,
  Sparkles,
  Link2,
  ExternalLink,
  Share2,
  MessageCircle,
  Instagram,
  MessageSquare,
  Mail,
} from 'lucide-react';
import { GoogleOAuthProvider } from '@react-oauth/google';
// 🔐 AUTH & CONTEXTS
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { ToastRenderer } from './components/ui/ToastRenderer';

// 🎨 COMPONENTS CRITIQUES (Chargés immédiatement)
import Layout from './components/layout/Layout';
import CrispChat from './components/CrispChat';
import RealtimeNotificationToasts from './components/RealtimeNotificationToasts';

// 🎨 MODALS (Lazy-loaded - pas nécessaires au premier rendu)
const QuickAuthModal = lazy(() =>
  import('./components/selling').then(m => ({ default: m.QuickAuthModal }))
);
const SimpleCreateProductModal = lazy(() =>
  import('./components/selling').then(m => ({
    default: m.SimpleCreateProductModal,
  }))
);
const PublishChoiceModal = lazy(
  () => import('./components/selling/PublishChoiceModal')
);
const SubscriptionSuccessPage = lazy(
  () => import('./pages/subscription/SubscriptionSuccessPage')
);
const CartModal = lazy(() =>
  import('./components/cart/CartModal').then(m => ({ default: m.CartModal }))
);
const EmailVerificationModal = lazy(() =>
  import('./components/auth').then(m => ({ default: m.EmailVerificationModal }))
);
const WelcomeVerifiedModal = lazy(() =>
  import('./components/auth').then(m => ({ default: m.WelcomeVerifiedModal }))
);
const OnboardingModal = lazy(
  () => import('./components/onboarding/OnboardingModal')
);
const SellerOnboardingModal = lazy(
  () => import('./components/onboarding/SellerOnboardingModal')
);
import SaleToast, { type SaleData } from './components/notifications/SaleToast';
import wsService from './services/websocketService';
import referralService from './services/referralService';
import {
  ProfilesProvider,
  useProfilesContext,
} from './contexts/ProfilesContext';
import { UpsellProvider } from './contexts/UpsellContext';
// 🌍 I18N
import './i18n';

// ⚡ LAZY LOADING DES PAGES (Code Splitting automatique)
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductsPage = lazy(
  () => import('./components/marketplace/ProductsPage')
);
const ProductDetail = lazy(
  () => import('./components/marketplace/ProductDetail')
);
const SellerShop = lazy(() => import('./components/marketplace/SellerShop'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const ProfileDetails = lazy(
  () => import('./components/profiles/ProfileDetails')
);
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const MessagesPage = lazy(() =>
  import('./pages/messages/MessagesPage').then(module => ({
    default: module.MessagesPage,
  }))
);
const SubscriptionPage = lazy(
  () => import('./pages/subscription/SubscriptionPage')
);
const PricingPage = lazy(() => import('./pages/PricingPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const SellerDashboardPage = lazy(() => import('./pages/SellerDashboardPage'));
const ImportFromScreenshotsPage = lazy(
  () => import('./pages/ImportFromScreenshotsPage')
);
const BuyerDashboardPage = lazy(() => import('./pages/BuyerDashboardPage'));
const CoachmarkDemoPage = lazy(() => import('./pages/CoachmarkDemoPage'));
const TutorialDemoPage = lazy(() => import('./pages/TutorialDemoPage'));
const SellerMissionDetailPage = lazy(
  () => import('./pages/SellerMissionDetailPage')
);
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const UnsubscribedPage = lazy(() => import('./pages/UnsubscribedPage'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
// SellerAddressCaptureModal n'est plus utilise dans App.tsx (la gate
// publication a ete supprimee). La modal vit maintenant uniquement sur
// OrderDetailPage au moment 'Creer mon etiquette'.
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ProductEditPage = lazy(() => import('./pages/ProductEditPage'));
const ProductStatsPage = lazy(() => import('./pages/ProductStatsPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const ExplorerPage = lazy(() => import('./pages/ExplorerPage'));
const FamilyLandingPage = lazy(() => import('./pages/FamilyLandingPage'));
const VacancesPage = lazy(() => import('./pages/VacancesPage'));
const CadeauMaitressePage = lazy(() => import('./pages/CadeauMaitressePage'));
const CadeauNaissancePage = lazy(() => import('./pages/CadeauNaissancePage'));
const GrandesOccasionsPage = lazy(() => import('./pages/GrandesOccasionsPage'));
const MomentsPage = lazy(() => import('./pages/MomentsPage'));
const CommunityFeedPage = lazy(() => import('./pages/CommunityFeedPage'));
const MissionResultsPage = lazy(() => import('./pages/MissionResultsPage'));
const MissionsListPage = lazy(() => import('./pages/MissionsListPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const DisputesPage = lazy(() => import('./pages/DisputesPage'));
const CGUPage = lazy(() => import('./pages/CGUPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const MentionsLegalesPage = lazy(() => import('./pages/MentionsLegalesPage'));
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage'));

// 🎨 LOADING FALLBACK PREMIUM
const PageLoader: React.FC = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ backgroundColor: 'var(--beige-50, #f8f6f0)' }}
  >
    <div className="text-center">
      <div
        className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
        style={{ color: 'var(--brand-500, #d4a574)' }}
        role="status"
        aria-label="Chargement en cours"
      >
        <span className="sr-only">Chargement...</span>
      </div>
      <p className="mt-4 text-sm" style={{ color: 'var(--gray-600, #6b7280)' }}>
        Chargement...
      </p>
    </div>
  </div>
);

// 🔒 PROTECTED ROUTE WRAPPER
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = false,
  requireAdmin = false,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) return <PageLoader />;

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (
    requireAdmin &&
    (!user || !['ADMIN', 'SUPER_ADMIN'].includes(user.role))
  ) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// 📦 ROUTE WRAPPERS OPTIMISÉS AVEC MÉMOIZATION
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { glowpNavigate, smartBack } from './lib/navigate';
const ProductsPageWrapper = React.memo(
  ({ onShowLogin }: { onShowLogin: () => void }) => {
    const navigate = useNavigate();

    return (
      <Suspense fallback={<PageLoader />}>
        <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
          <ProductsPage
            onBack={() => smartBack(navigate, '/home')}
            onProductClick={id => navigate(`/product/${id}`)}
            onShowLogin={onShowLogin}
          />
        </div>
      </Suspense>
    );
  }
);
ProductsPageWrapper.displayName = 'ProductsPageWrapper';

const ProductDetailWrapper = React.memo(
  ({
    onShowLogin,
    onOpenCart,
  }: {
    onShowLogin: () => void;
    onOpenCart: () => void;
  }) => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const shopTheme = searchParams.get('shopTheme') || undefined;
    const fromSeller = searchParams.get('fromSeller') || undefined;

    if (!productId) {
      return <Navigate to="/products" replace />;
    }

    return (
      <Suspense fallback={<PageLoader />}>
        <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
          <ProductDetail
            productId={productId}
            onBack={() => {
              // VRAI retour browser en priorite : ramene au scroll exact
              // d'ou l'user vient (catalogue, profil, favoris, search...).
              // fromSeller fallback : si l'user arrive direct par lien
              // (push notif, partage), on tente la boutique d'origine si
              // connue, sinon le catalogue.
              smartBack(
                navigate,
                fromSeller ? `/seller/${fromSeller}` : '/products'
              );
            }}
            onSellerShopClick={sellerId => navigate(`/seller/${sellerId}`)}
            onShowLogin={onShowLogin}
            onProductClick={id => {
              // Propage fromSeller dans la chaine produit -> produit
              // similaire pour que le retour respecte la boutique d'origine.
              const params = new URLSearchParams();
              if (fromSeller) params.set('fromSeller', fromSeller);
              if (shopTheme) params.set('shopTheme', shopTheme);
              const qs = params.toString();
              navigate(`/product/${id}${qs ? `?${qs}` : ''}`);
            }}
            onNavigateToMessages={convId =>
              navigate(convId ? `/messages/${convId}` : '/messages')
            }
            onOpenCart={onOpenCart}
            shopTheme={shopTheme}
          />
        </div>
      </Suspense>
    );
  }
);

const ProductEditWrapper: React.FC = () => {
  return (
    <ProtectedRoute requireAuth>
      <Suspense fallback={<PageLoader />}>
        <ProductEditPage />
      </Suspense>
    </ProtectedRoute>
  );
};

const ProductStatsWrapper: React.FC = () => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <ProductStatsPage />
    </Suspense>
  </ProtectedRoute>
);

const WalletWrapper: React.FC = () => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <WalletPage />
    </Suspense>
  </ProtectedRoute>
);

const FavoritesWrapper: React.FC = () => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <FavoritesPage />
    </Suspense>
  </ProtectedRoute>
);

const NotificationsWrapper: React.FC = () => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <NotificationsPage />
    </Suspense>
  </ProtectedRoute>
);

const DisputesWrapper: React.FC = () => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <DisputesPage />
    </Suspense>
  </ProtectedRoute>
);

const ProductDuplicateWrapper: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('products');

  React.useEffect(() => {
    if (!productId) {
      navigate('/');
      return;
    }

    const duplicate = async () => {
      const token = localStorage.getItem('glowp_access_token');
      const res = await fetch(`${API_BASE_URL}/api/v1/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) {
        navigate('/');
        return;
      }

      const p = data.data.product;
      const translation = p.product_translations?.[0];

      const body = {
        title: `${translation?.title || t('product.exampleProduct')} (${t('common:buttons.copy', 'copie')})`,
        description: translation?.description || '',
        price: p.price,
        brand: p.brand,
        condition: p.condition,
        size: p.size,
        color: p.color,
        material: p.material,
        category_id: p.category_id,
        status: 'ACTIVE',
        imageUrls: (p.product_images || []).map((img: any) => img.url),
      };

      const createRes = await fetch(`${API_BASE_URL}/api/v1/products`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const createData = await createRes.json();

      if (createData.success && createData.data?.product?.id) {
        navigate(`/product/edit/${createData.data.product.id}`);
      } else {
        navigate('/account?tab=shop');
      }
    };

    duplicate();
  }, [productId, navigate]);

  return (
    <ProtectedRoute requireAuth>
      <div className="min-h-screen flex items-center justify-center bg-brand-offwhite">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto mb-3" />
          <p className="text-brand-warm text-sm">Duplication en cours...</p>
        </div>
      </div>
    </ProtectedRoute>
  );
};

const SellerShopWrapper = React.memo(
  ({ onShowLogin }: { onShowLogin: () => void }) => {
    const { sellerId } = useParams<{ sellerId: string }>();
    const navigate = useNavigate();

    // Capturer le code parrainage depuis l'URL de la boutique (?ref=CODE)
    useEffect(() => {
      const hash = window.location.hash || '';
      const queryPart = hash.split('?')[1] || '';
      const params = new URLSearchParams(queryPart);
      const ref = params.get('ref');
      if (ref) {
        localStorage.setItem('glowp_referral_code', ref.toUpperCase().trim());
      }
    }, []);

    if (!sellerId) {
      return <Navigate to="/products" replace />;
    }

    return (
      <Suspense fallback={<PageLoader />}>
        <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
          <SellerShop
            sellerId={sellerId}
            onBack={() => smartBack(navigate, '/products')}
            onShowLogin={onShowLogin}
            onProductClick={(id, shopTheme) => {
              // fromSeller TOUJOURS passe (bug 18/05 : si shopTheme vide,
              // fromSeller etait perdu -> retour ramenait au catalogue
              // au lieu de la boutique d'origine).
              const params = new URLSearchParams({ fromSeller: sellerId });
              if (shopTheme) params.set('shopTheme', shopTheme);
              navigate(`/product/${id}?${params.toString()}`);
            }}
          />
        </div>
      </Suspense>
    );
  }
);
SellerShopWrapper.displayName = 'SellerShopWrapper';

const ProfileDetailsWrapper = React.memo(
  ({ onShowCreateProduct }: { onShowCreateProduct: () => void }) => {
    const { profileId } = useParams<{ profileId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    if (!profileId) {
      return <Navigate to="/home" replace />;
    }

    return (
      <Suspense fallback={<PageLoader />}>
        <div
          className="min-h-screen"
          style={{ backgroundColor: 'var(--beige-50)' }}
        >
          <ProfileDetails
            profileId={profileId}
            onBack={() => smartBack(navigate, '/home')}
            onCreateProduct={onShowCreateProduct}
            onNavigateToSubscription={() => navigate('/subscription')}
            onNavigateToAdmin={
              user?.role && ['ADMIN', 'SUPER_ADMIN'].includes(user.role)
                ? () => navigate('/admin')
                : undefined
            }
          />
        </div>
      </Suspense>
    );
  }
);
ProfileDetailsWrapper.displayName = 'ProfileDetailsWrapper';

const CheckoutWrapper = React.memo(() => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();

  if (!vendorId) {
    return <Navigate to="/products" replace />;
  }

  return (
    <ProtectedRoute requireAuth>
      <Suspense fallback={<PageLoader />}>
        <div className="min-h-screen" style={{ backgroundColor: '#f9fafb' }}>
          <CheckoutPage vendorId={vendorId} onBack={() => navigate(-1)} />
        </div>
      </Suspense>
    </ProtectedRoute>
  );
});
CheckoutWrapper.displayName = 'CheckoutWrapper';

const CheckoutSuccessWrapper = React.memo(() => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <div className="min-h-screen bg-brand-page">
        <CheckoutSuccessPage />
      </div>
    </Suspense>
  </ProtectedRoute>
));
CheckoutSuccessWrapper.displayName = 'CheckoutSuccessWrapper';

const CheckoutCancelWrapper = React.memo(() => (
  <Suspense fallback={<PageLoader />}>
    <CheckoutCancelPage />
  </Suspense>
));
CheckoutCancelWrapper.displayName = 'CheckoutCancelWrapper';

const MessagesWrapper = React.memo(() => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();

  return (
    <ProtectedRoute requireAuth>
      <Suspense fallback={<PageLoader />}>
        <div
          className="min-h-screen"
          style={{ backgroundColor: 'var(--beige-50)' }}
        >
          <MessagesPage
            onBack={() => smartBack(navigate, '/home')}
            conversationId={conversationId}
          />
        </div>
      </Suspense>
    </ProtectedRoute>
  );
});
MessagesWrapper.displayName = 'MessagesWrapper';

const AdminWrapper = React.memo(() => {
  const navigate = useNavigate();

  return (
    <ProtectedRoute requireAuth requireAdmin>
      <Suspense fallback={<PageLoader />}>
        <AdminDashboard onBackToApp={() => navigate('/home')} />
      </Suspense>
    </ProtectedRoute>
  );
});
AdminWrapper.displayName = 'AdminWrapper';

const SubscriptionWrapper = React.memo(() => {
  const navigate = useNavigate();

  return (
    <ProtectedRoute requireAuth>
      <Suspense fallback={<PageLoader />}>
        <SubscriptionPage onBack={() => smartBack(navigate, '/home')} />
      </Suspense>
    </ProtectedRoute>
  );
});
SubscriptionWrapper.displayName = 'SubscriptionWrapper';

const OrdersWrapper = React.memo(() => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <OrdersPage />
    </Suspense>
  </ProtectedRoute>
));
OrdersWrapper.displayName = 'OrdersWrapper';

const OrderDetailWrapper = React.memo(() => {
  const { orderId } = useParams<{ orderId: string }>();

  if (!orderId) {
    return <Navigate to="/orders" replace />;
  }

  return (
    <ProtectedRoute requireAuth>
      <Suspense fallback={<PageLoader />}>
        <OrderDetailPage />
      </Suspense>
    </ProtectedRoute>
  );
});
OrderDetailWrapper.displayName = 'OrderDetailWrapper';

const SellerDashboardWrapper = React.memo(() => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <SellerDashboardPage />
    </Suspense>
  </ProtectedRoute>
));
SellerDashboardWrapper.displayName = 'SellerDashboardWrapper';

const ImportFromScreenshotsWrapper = React.memo(() => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <ImportFromScreenshotsPage />
    </Suspense>
  </ProtectedRoute>
));
ImportFromScreenshotsWrapper.displayName = 'ImportFromScreenshotsWrapper';

const BuyerDashboardWrapper = React.memo(() => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <BuyerDashboardPage />
    </Suspense>
  </ProtectedRoute>
));
BuyerDashboardWrapper.displayName = 'BuyerDashboardWrapper';

const ProfilePageWrapper = React.memo(() => (
  <ProtectedRoute requireAuth>
    <Suspense fallback={<PageLoader />}>
      <div className="min-h-screen bg-brand-page">
        <ProfilePage />
      </div>
    </Suspense>
  </ProtectedRoute>
));
ProfilePageWrapper.displayName = 'ProfilePageWrapper';

const VerifyEmailWrapper = React.memo(() => (
  <Suspense fallback={<PageLoader />}>
    <VerifyEmail />
  </Suspense>
));
VerifyEmailWrapper.displayName = 'VerifyEmailWrapper';

const ForgotPasswordWrapper = React.memo(() => {
  const navigate = useNavigate();

  return (
    <Suspense fallback={<PageLoader />}>
      <ForgotPassword onBack={() => smartBack(navigate, '/')} />
    </Suspense>
  );
});
ForgotPasswordWrapper.displayName = 'ForgotPasswordWrapper';

const ResetPasswordWrapper = React.memo(() => {
  const navigate = useNavigate();

  return (
    <Suspense fallback={<PageLoader />}>
      <ResetPassword onLoginClick={() => navigate('/')} />
    </Suspense>
  );
});
ResetPasswordWrapper.displayName = 'ResetPasswordWrapper';

const RegisterRedirectWrapper = React.memo(() => {
  // Capturer le code parrainage avant redirect
  const hash = window.location.hash || '';
  const queryPart = hash.split('?')[1] || '';
  const params = new URLSearchParams(queryPart);
  const ref = params.get('ref');
  if (ref) {
    localStorage.setItem('glowp_referral_code', ref.toUpperCase().trim());
  }
  return <Navigate to="/" replace />;
});
RegisterRedirectWrapper.displayName = 'RegisterRedirectWrapper';

// 🎯 COMPOSANT PRINCIPAL OPTIMISÉ
function AppContent() {
  const { t, i18n } = useTranslation(['common', 'auth']);
  const { user, isAuthenticated, getProfile, logout } = useAuth();

  // [Page view tracking déplacé dans Layout via useLocation +
  // behaviorTracking.pageView() — fire à chaque navigation, anonymes
  // inclus, plus de polling 3s.]
  const { showSuccess, showError } = useToast();
  const {
    needsOnboarding,
    createProfile,
    updateProfile,
    switchProfile,
    parentProfile,
    childCount,
    isLoading: profilesLoading,
  } = useProfilesContext();
  // Re-proposé après 72h si "Plus tard" cliqué, définitivement si complété
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    const until = localStorage.getItem('glowp_onboarding_dismissed_until');
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
  });

  // 📦 ÉTATS GLOBAUX AVEC PERSISTANCE
  const [isCartModalOpen, setIsCartModalOpen] = useState(() => {
    const saved = localStorage.getItem('glowp-cartModalOpen');
    return saved === 'true';
  });

  // Listener event global pour ouvrir le panier depuis n'importe quel
  // composant (Layout header, par ex). Decouple Layout d'App.tsx.
  useEffect(() => {
    const handler = () => setIsCartModalOpen(true);
    window.addEventListener('glowp:openCart', handler);
    return () => window.removeEventListener('glowp:openCart', handler);
  }, []);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'register'>(
    'login'
  );
  const [message, setMessage] = useState('');
  const [showQuickAuth, setShowQuickAuth] = useState(false);
  const [quickAuthMode, setQuickAuthMode] = useState<'login' | 'register'>(
    'register'
  );
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  // Note : showAddressCapture a ete retire de la publication. La modal
  // de capture d'adresse vit maintenant uniquement sur OrderDetailPage
  // au moment de creer le bordereau (apres 1ere vente). Plus de friction
  // au moment Publier.
  /** Choice modal "Une annonce vs Importer en bulk" qui s'ouvre par
   *  défaut sur tous les CTA "Vendre" / "Publier" — visibilité maximale
   *  du bulk import au moment psychologique pertinent. */
  const [showPublishChoice, setShowPublishChoice] = useState(false);
  const [showSellerOnboarding, setShowSellerOnboarding] = useState(false);
  const [showSellerOnboardingCTA, setShowSellerOnboardingCTA] = useState(false);
  const [shopLinkCopied, setShopLinkCopied] = useState(false);
  // Code parrainage du user connecte. Ajoute aux URLs partagees pour
  // attribuer les signups/achats au parrain. Fetch lazy quand le bottom
  // sheet s'apprete a s'afficher (evite un appel API inutile au mount).
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [showEmailVerificationModal, setShowEmailVerificationModal] =
    useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [showWelcomeVerified, setShowWelcomeVerified] = useState(false);
  const [welcomeFirstName, setWelcomeFirstName] = useState('');

  // Rappel doux "ajouter vos enfants" — s'affiche 72h apres le skip si aucun enfant cree
  const [showKidsReminder, setShowKidsReminder] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || profilesLoading) return;
    const reminderAt = localStorage.getItem('glowp_kids_reminder_at');
    if (!reminderAt) return;
    const ts = parseInt(reminderAt, 10);
    if (Date.now() < ts) return; // pas encore 72h
    if (childCount > 0) {
      // deja rempli entre-temps — supprimer le rappel
      localStorage.removeItem('glowp_kids_reminder_at');
      return;
    }
    setShowKidsReminder(true);
  }, [isAuthenticated, profilesLoading, childCount]);

  // Refs stables pour éviter les stale closures dans les callbacks montés une seule fois
  const getProfileRef = useRef(getProfile);
  const showSuccessRef = useRef(showSuccess);
  const showErrorRef = useRef(showError);
  const setWelcomeFirstNameRef = useRef(setWelcomeFirstName);
  const setShowWelcomeVerifiedRef = useRef(setShowWelcomeVerified);
  const setOnboardingDismissedRef = useRef(setOnboardingDismissed);
  const setShowCreateProductRef = useRef(setShowCreateProduct);
  useEffect(() => {
    getProfileRef.current = getProfile;
    showSuccessRef.current = showSuccess;
    showErrorRef.current = showError;
    setWelcomeFirstNameRef.current = setWelcomeFirstName;
    setShowWelcomeVerifiedRef.current = setShowWelcomeVerified;
    setOnboardingDismissedRef.current = setOnboardingDismissed;
    setShowCreateProductRef.current = setShowCreateProduct;
  });

  // 📧 Échange de code post-vérification email → connexion automatique
  // useCallback([]) = fonction stable, jamais recréée, lit toujours les refs à jour
  const exchangeCodeAndLogin = useCallback(
    async (code: string, firstName: string) => {
      try {
        const [{ default: apiClient }, { API_BASE_URL }] = await Promise.all([
          import('./services/apiClient'),
          import('./config/api'),
        ]);

        const wasSelling =
          localStorage.getItem('glowp_sell_pending') === 'true';

        const res = await fetch(`${API_BASE_URL}/api/v1/auth/exchange-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = await res.json();

        if (!data.success || !data.data?.tokens) {
          showErrorRef.current(t('auth:verification.linkExpired'));
          return;
        }

        const { accessToken, refreshToken } = data.data.tokens;
        const resolvedFirstName = data.data.firstName || firstName;

        if (wasSelling) {
          localStorage.removeItem('glowp_sell_pending');
          localStorage.setItem('glowp_onboarding_after_sale', 'true');
          localStorage.setItem(
            'glowp_onboarding_dismissed_until',
            String(Number.MAX_SAFE_INTEGER)
          );
          setOnboardingDismissedRef.current(true);
        }

        apiClient.setTokens(accessToken, refreshToken);
        await getProfileRef.current();

        showSuccessRef.current(t('auth:verification.emailVerifiedWelcome'));

        if (wasSelling) {
          setTimeout(() => setShowCreateProductRef.current(true), 300);
        } else {
          localStorage.removeItem('glowp_onboarding_dismissed_until');
          setOnboardingDismissedRef.current(false);
          setWelcomeFirstNameRef.current(resolvedFirstName);
          setShowWelcomeVerifiedRef.current(true);
        }
      } catch (err) {
        showErrorRef.current(
          t('auth:verification.connectionError', {
            message: err instanceof Error ? err.message : String(err),
          })
        );
      }
    },
    []
  );

  // 📧 Vérification directe du token email (lien frontend-first, SafeLinks-safe)
  // Le backend renvoie des JWT directement quand appelé via fetch (pas de redirect)
  const verifyTokenAndLogin = useCallback(
    async (token: string, firstName: string) => {
      try {
        const [{ default: apiClient }, { API_BASE_URL }] = await Promise.all([
          import('./services/apiClient'),
          import('./config/api'),
        ]);

        const res = await fetch(
          `${API_BASE_URL}/api/v1/auth/verify-email?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();

        if (!data.success || !data.data?.tokens) {
          showErrorRef.current(t('auth:verification.linkInvalidOrExpired'));
          return;
        }

        const { accessToken, refreshToken } = data.data.tokens;
        const resolvedFirstName =
          data.data.user?.first_name || data.data.firstName || firstName;

        localStorage.removeItem('glowp_onboarding_dismissed_until');
        setOnboardingDismissedRef.current(false);
        apiClient.setTokens(accessToken, refreshToken);
        await getProfileRef.current();

        showSuccessRef.current(t('auth:verification.emailVerifiedWelcome'));
        setWelcomeFirstNameRef.current(resolvedFirstName);
        setShowWelcomeVerifiedRef.current(true);
      } catch (err) {
        showErrorRef.current(
          t('auth:verification.genericError', {
            message: err instanceof Error ? err.message : String(err),
          })
        );
      }
    },
    []
  );

  // Web : detecte token ou code d'echange. On lit AUTANT le hash (URLs
  // legacy en circulation) que le search (nouveau format apres fix
  // feedback_no_hash_in_email_urls). Priorite au search car c'est le
  // nouveau format officiel.
  useEffect(() => {
    const search = window.location.search;
    const hash = window.location.hash;

    // Source d'abord : query string (nouveau format SafeLinks-safe)
    let token: string | null = null;
    let code: string | null = null;
    let firstName = '';

    if (search.includes('token=') || search.includes('code=')) {
      const searchParams = new URLSearchParams(search);
      token = searchParams.get('token');
      code = searchParams.get('code');
      firstName = searchParams.get('firstName') ?? '';
    } else if (hash.includes('token=') || hash.includes('code=')) {
      // Fallback : ancien format hash (liens emails deja envoyes avant le fix)
      const queryPart = hash.includes('?') ? hash.split('?')[1] : '';
      const hashParams = new URLSearchParams(queryPart);
      token = hashParams.get('token');
      code = hashParams.get('code');
      firstName = hashParams.get('firstName') ?? '';
    }

    if (!token && !code) return;

    // Nettoyer l'URL immediatement pour eviter le flash du formulaire creation.
    // On retire BOTH le hash et les params token/code du search pour repartir
    // sur une URL propre.
    const cleanSearch = new URLSearchParams(window.location.search);
    cleanSearch.delete('token');
    cleanSearch.delete('code');
    cleanSearch.delete('firstName');
    const cleanSearchString = cleanSearch.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname +
        (cleanSearchString ? `?${cleanSearchString}` : '')
    );
    localStorage.removeItem('glowp_sell_pending');

    if (token) {
      verifyTokenAndLogin(token, firstName);
    } else if (code) {
      exchangeCodeAndLogin(code, firstName);
    }
  }, [verifyTokenAndLogin, exchangeCodeAndLogin]);

  // 📱 Deep link Capacitor (mobile)
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const setupDeepLink = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');

        const launchUrl = await CapApp.getLaunchUrl();
        if (launchUrl?.url) {
          const parsed = new URL(launchUrl.url);
          const isExchange =
            parsed.protocol === 'glowp:' &&
            (parsed.hostname === 'exchange' ||
              parsed.pathname.includes('exchange'));
          if (isExchange) {
            const code = parsed.searchParams.get('code');
            const firstName = parsed.searchParams.get('firstName') ?? '';
            if (code) await exchangeCodeAndLogin(code, firstName);
          }
        }

        const listener = await CapApp.addListener(
          'appUrlOpen',
          async ({ url }) => {
            const parsed = new URL(url);
            const isExchange =
              parsed.protocol === 'glowp:' &&
              (parsed.hostname === 'exchange' ||
                parsed.pathname.includes('exchange'));
            if (isExchange) {
              const code = parsed.searchParams.get('code');
              const firstName = parsed.searchParams.get('firstName') ?? '';
              if (code) await exchangeCodeAndLogin(code, firstName);
            }
          }
        );
        cleanup = () => listener.remove();
      } catch {
        // Pas sur mobile (web) — pas de deep link
      }
    };

    setupDeepLink();
    return () => cleanup?.();
  }, [exchangeCodeAndLogin]);

  // 🛒 Vente en attente après onboarding (quand l'user se connecte via bouton "+")
  const [sellPendingAfterOnboarding, setSellPendingAfterOnboarding] =
    useState(false);
  const [pendingSale, setPendingSale] = useState<SaleData | null>(null);

  // 🛒 Après connexion : si une vente était en attente (via bouton "+"), ouvrir le formulaire
  // On attend que les profils soient chargés (profilesLoading=false) pour connaître needsOnboarding
  useEffect(() => {
    // CRITIQUE : on attend que le welcome screen QuickAuthModal soit ferme
    // (= la user a cliqué un des 3 boutons "Ajouter / Personnaliser /
    // Explorer"). Sinon ce useEffect consomme `glowp_sell_pending` (posé par
    // handleSell AVANT auth) au moment où isAuthenticated passe à true,
    // ce qui ouvre PublishChoiceModal SOUS le welcome screen — la user
    // clique "Personnaliser ma boutique" mais atterrit sur "+ annonce /
    // importer bulk". `routePostAuthIntent` (déclenche par onSuccess du
    // welcome button) consomme proprement les flags donc on n a pas
    // besoin de cette consommation en useEffect quand l user est encore
    // dans le flow d auth.
    if (showQuickAuth || showLoginModal) return;
    if (isAuthenticated && user && !profilesLoading) {
      const sellPending = localStorage.getItem('glowp_sell_pending');
      if (sellPending === 'true') {
        localStorage.removeItem('glowp_sell_pending');
        // Vendeur → ouvrir la choice modal (manuel vs bulk) pour montrer
        // les 2 options dès la 1ère publication, pas l'onboarding bloquant
        localStorage.setItem('glowp_onboarding_after_sale', 'true');
        localStorage.setItem(
          'glowp_onboarding_dismissed_until',
          String(Number.MAX_SAFE_INTEGER)
        );
        setOnboardingDismissed(true);
        setShowPublishChoice(true);
      }

      const shopOnboardingPending = localStorage.getItem(
        'glowp_shop_onboarding_pending'
      );
      if (shopOnboardingPending === 'true') {
        localStorage.removeItem('glowp_shop_onboarding_pending');
        // CRITIQUE : defere OnboardingModal exactement comme le fait
        // routePostAuthIntent. Sans ca, race condition -> OnboardingModal
        // "Dites-nous en plus sur vous" s ouvre par-dessus / a la place
        // de SellerOnboardingModal quand la user clique "Personnaliser
        // ma boutique" sur le welcome screen QuickAuthModal.
        localStorage.setItem('glowp_onboarding_after_sale', 'true');
        localStorage.setItem(
          'glowp_onboarding_dismissed_until',
          String(Number.MAX_SAFE_INTEGER)
        );
        setOnboardingDismissed(true);
        setTimeout(() => setShowSellerOnboarding(true), 300);
      }
    }
  }, [
    isAuthenticated,
    user?.id,
    needsOnboarding,
    onboardingDismissed,
    profilesLoading,
    showQuickAuth,
    showLoginModal,
  ]);

  // 🔐 Sur Capacitor : session expirée → logout propre sans reload page
  useEffect(() => {
    const handler = () => {
      setShowCreateProduct(false);
      setShowQuickAuth(false);
      logout();
    };
    window.addEventListener('auth:logout-required', handler);
    return () => window.removeEventListener('auth:logout-required', handler);
  }, [logout]);

  // 🎉 SALE TOAST — écoute les notifications PRODUCT_SOLD en temps réel
  useEffect(() => {
    if (!isAuthenticated) return;
    const off = wsService.onNewNotification(notif => {
      if (notif.type === 'PRODUCT_SOLD' && notif.data) {
        setPendingSale({
          orderId: String(notif.data.orderId ?? ''),
          orderNumber: notif.data.orderNumber
            ? String(notif.data.orderNumber)
            : undefined,
          productTitle: String(notif.data.productTitle ?? ''),
          netAmount: Number(notif.data.netAmount ?? 0),
          isFirstSale: Boolean(notif.data.isFirstSale),
        });
      }
    });
    return () => {
      off?.();
    };
  }, [isAuthenticated]);

  // 🌍 PERSISTANCE LANGUE GLOBALE (Optimisé)
  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang && i18n.language !== savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  // ✅ FORCE CHARGEMENT NAMESPACE PRODUCTS
  useEffect(() => {
    i18n.loadNamespaces('products').then(() => {});
  }, [i18n]);

  useEffect(() => {
    const handleLanguageChange = (newLang: string) => {
      localStorage.setItem('i18nextLng', newLang);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => i18n.off('languageChanged', handleLanguageChange);
  }, [i18n]);

  // 💾 Sauvegarde état CartModal
  useEffect(() => {
    localStorage.setItem('glowp-cartModalOpen', String(isCartModalOpen));
  }, [isCartModalOpen]);

  // 🎯 HANDLERS OPTIMISÉS AVEC useCallback
  const handleSell = useCallback(() => {
    if (isAuthenticated && user) {
      // On ouvre la choice modal (Une annonce vs Importer bulk) au lieu
      // d'aller direct sur SimpleCreateProductModal. La bulk import doit
      // être visible à tous les points d'entrée "Vendre" / "Publier",
      // pas juste sur le seller dashboard.
      setShowPublishChoice(true);
    } else {
      localStorage.setItem('glowp_sell_pending', 'true');
      setQuickAuthMode('register');
      setShowQuickAuth(true);
    }
  }, [isAuthenticated, user]);

  // 🛍️ glowp:openSell — déclenché depuis SaleToast "Vendre encore"
  useEffect(() => {
    const handler = () => handleSell();
    window.addEventListener('glowp:openSell', handler);
    return () => window.removeEventListener('glowp:openSell', handler);
  }, [handleSell]);

  // 📝 glowp:openRegister — déclenché depuis la homepage
  useEffect(() => {
    const handler = () => {
      setLoginModalMode('register');
      setShowLoginModal(true);
    };
    window.addEventListener('glowp:openRegister', handler);
    return () => window.removeEventListener('glowp:openRegister', handler);
  }, []);

  // Routing post-authentification base sur l'intention exprimee juste
  // avant via flags localStorage. Partage entre les 2 instances de
  // QuickAuthModal (showLoginModal du header, showQuickAuth du bouton "+")
  // pour que les boutons de l'ecran de bienvenue post-inscription marchent
  // peu importe par ou la user est entree dans l'auth.
  //
  // Strategie publish-first / shop-onboarding-first :
  // - "Ajouter mon premier article" → on respecte l'intention emotionnelle,
  //   PublishChoice direct. L'OnboardingModal profil parent/enfants est
  //   defere via glowp_onboarding_after_sale et propose APRES la 1ere
  //   publication, quand sa valeur (recommandations personnalisees) devient
  //   evidente. Cassure de momentum = abandon = pas de 1ere annonce.
  // - "Personnaliser ma boutique" → SellerOnboardingModal direct, l'intention
  //   est deja de construire son univers, l'onboarding plus profond est OK.
  //   Meme logique : on bloque l'OnboardingModal profil le temps qu'elle
  //   finisse, pour eviter l'empilement de modals.
  // Retourne true si une intention a ete routee.
  const routePostAuthIntent = useCallback((): boolean => {
    const shopOnboardingPending =
      localStorage.getItem('glowp_shop_onboarding_pending') === 'true';
    const sellPending = localStorage.getItem('glowp_sell_pending') === 'true';

    // Important : on pose le verrou anti-empilement de l'OnboardingModal
    // profil INCONDITIONNELLEMENT (pas seulement si needsOnboarding=true
    // au moment du click). Pour un user fraichement inscrit, les profiles
    // ne sont pas encore charges au moment du click, donc needsOnboarding
    // est encore false. Sans le verrou, des que la fetch profiles termine
    // et que needsOnboarding flip a true, l'OnboardingModal s'ouvre
    // par-dessus PublishChoice/SellerOnboarding. handleCreateProductSuccess
    // (et le close de SellerOnboarding) consomment ces flags pour reactiver
    // l'OnboardingModal au bon moment. Si needsOnboarding reste false
    // (user qui a deja ses profils), le flag dort dans localStorage et ne
    // change rien — handleCreateProductSuccess le nettoie quand meme.
    const deferProfileOnboarding = () => {
      localStorage.setItem('glowp_onboarding_after_sale', 'true');
      localStorage.setItem(
        'glowp_onboarding_dismissed_until',
        String(Number.MAX_SAFE_INTEGER)
      );
      setOnboardingDismissed(true);
    };

    if (shopOnboardingPending) {
      localStorage.removeItem('glowp_shop_onboarding_pending');
      localStorage.removeItem('glowp_sell_pending');
      deferProfileOnboarding();
      setTimeout(() => setShowSellerOnboarding(true), 300);
      return true;
    }

    if (sellPending) {
      localStorage.removeItem('glowp_sell_pending');
      deferProfileOnboarding();
      setShowPublishChoice(true);
      return true;
    }

    return false;
  }, []);

  const handleQuickAuthSuccess = useCallback(() => {
    setShowQuickAuth(false);
    routePostAuthIntent();
  }, [routePostAuthIntent]);

  const handleCreateProductSuccess = useCallback(
    (productId?: string) => {
      setShowCreateProduct(false);
      const message = productId
        ? t(
            'products.createSuccess',
            `Annonce créée avec succès ! ID: ${productId}`
          )
        : t('products.createSuccess', `Annonce créée avec succès !`);
      setMessage(message);

      // Nudge "Verifie ton email" — UNIQUEMENT 1 fois apres la 1ere
      // publication, si l email n est pas encore verifie. Memoire valide
      // par Marie-Amelie : email verifie = user joignable (notifs,
      // reset password, alerts). Sans ce nudge, plein de comptes
      // fantomes qui ont publie mais qu on ne peut pas atteindre.
      const verifNudgeKey = 'glowp_email_verif_nudge_shown';
      if (user && !user.isVerified && !localStorage.getItem(verifNudgeKey)) {
        localStorage.setItem(verifNudgeKey, '1');
        // Differe pour laisser le toast publication s afficher d abord.
        setTimeout(() => {
          setVerificationEmail(user.email || '');
          setShowEmailVerificationModal(true);
        }, 1500);
      }

      // Trigger feedback FIRST_PUBLISH (moment de fierte = taux de
      // reponse eleve). Differe pour ne pas bloquer le user en plein
      // moment d'euphorie post-publish (laisse aussi le toast confetti
      // s'afficher en premier). Skip si deja prompt pour ce context.
      import('./components/feedback/AppFeedbackPrompt').then(
        ({ hasBeenPrompted, triggerAppFeedback }) => {
          if (!hasBeenPrompted('FIRST_PUBLISH')) {
            setTimeout(() => triggerAppFeedback('FIRST_PUBLISH'), 4500);
          }
        }
      );

      // Si le vendeur a bypasse l'onboarding pour vendre en premier,
      // on le propose maintenant qu'il a publié son annonce
      const shouldShowOnboarding = localStorage.getItem(
        'glowp_onboarding_after_sale'
      );
      if (shouldShowOnboarding === 'true') {
        localStorage.removeItem('glowp_onboarding_after_sale');
        localStorage.removeItem('glowp_onboarding_dismissed_until');
        setTimeout(() => setOnboardingDismissed(false), 800);
      }

      // Apres publication, proposer le partage (et la personnalisation
      // si la boutique n'est pas encore customisee).
      // 1ere fois => affiche. Si dismiss via X / Voir / share => re-propose
      // dans 5 annonces. Le contenu s'adapte : Personnaliser n'apparait que
      // si le vendeur n'a pas encore termine l'onboarding boutique.
      // Logique avant : gate sur !sellerOnboardingDone => les vendeurs
      // installes ne voyaient JAMAIS le share UI. Casse pour SEVERINE etc.
      {
        const snoozeRaw = localStorage.getItem('glowp_seller_cta_snooze');
        const willShowCta =
          snoozeRaw === null ||
          (() => {
            const n = parseInt(snoozeRaw, 10);
            return Number.isFinite(n) && n <= 0;
          })();
        // Si on va afficher le bottom sheet, fetch en parallele le code
        // parrainage du user pour pouvoir l'injecter dans les URLs partagees.
        if (willShowCta && !referralCode) {
          referralService
            .getOrCreateCode()
            .then(d => setReferralCode(d?.referralCode || null))
            .catch(() => {});
        }
        if (snoozeRaw === null) {
          setTimeout(() => setShowSellerOnboardingCTA(true), 800);
        } else {
          const snooze = parseInt(snoozeRaw, 10);
          if (Number.isFinite(snooze) && snooze <= 0) {
            setTimeout(() => setShowSellerOnboardingCTA(true), 800);
          } else {
            localStorage.setItem(
              'glowp_seller_cta_snooze',
              String((Number.isFinite(snooze) ? snooze : 5) - 1)
            );
          }
        }
      }
    },
    [t, user]
  );

  const handleOnboardingUpdateParent = useCallback(
    async (data: {
      birthDate: string;
      gender: 'MALE' | 'FEMALE' | 'UNISEX';
      clothingSizes: string[];
      shoeSizes: string[];
      avatarUrl: string;
    }) => {
      if (!parentProfile?.id) return;
      try {
        setIsCreatingProfile(true);
        await updateProfile(parentProfile.id, {
          birth_date: data.birthDate || undefined,
          gender: data.gender.toLowerCase(), // backend attend 'male'/'female'/'unisex'
          avatar_url: data.avatarUrl || undefined,
          size_preferences: {
            clothing_sizes: data.clothingSizes,
            shoe_sizes: data.shoeSizes,
          },
        });
      } catch {
        // silently ignore — toast feedback handled at modal level
      } finally {
        setIsCreatingProfile(false);
      }
    },
    [updateProfile, parentProfile]
  );

  const handleOnboardingCreateChild = useCallback(
    async (childData: {
      name: string;
      birthDate: string;
      gender: 'MALE' | 'FEMALE' | 'UNISEX';
      clothingSizes: string[];
      shoeSizes: string[];
      avatarUrl: string;
    }) => {
      try {
        setIsCreatingProfile(true);
        const today = new Date();
        const birth = childData.birthDate
          ? new Date(childData.birthDate)
          : null;
        let ageInYears = 0; // défaut 0 si pas de date de naissance
        if (birth) {
          let totalMonths = (today.getFullYear() - birth.getFullYear()) * 12;
          totalMonths += today.getMonth() - birth.getMonth();
          if (today.getDate() < birth.getDate()) totalMonths--;
          ageInYears = Math.max(0, Math.floor(totalMonths / 12));
        }
        await createProfile({
          profile_name: childData.name,
          profile_type: 'CHILD',
          parent_profile_id: parentProfile?.id,
          age: ageInYears,
          birth_date: childData.birthDate || undefined,
          gender: childData.gender.toLowerCase(), // backend attend lowercase
          avatar_url: childData.avatarUrl || undefined,
          size_preferences: {
            clothing_sizes: childData.clothingSizes,
            shoe_sizes: childData.shoeSizes,
          },
        });
      } finally {
        setIsCreatingProfile(false);
      }
    },
    [createProfile, parentProfile]
  );

  // 🚦 ROUTER CONFIGURATION (Mémorisé pour éviter re-render)
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: (
            <Layout
              onShowLogin={() => {
                setLoginModalMode('login');
                setShowLoginModal(true);
              }}
              onShowRegister={() => {
                setLoginModalMode('register');
                setShowLoginModal(true);
              }}
              onShowSell={handleSell}
            />
          ),
          children: [
            {
              path: '/',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HomePage />
                </Suspense>
              ),
            },
            {
              path: '/home',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <HomePage />
                </Suspense>
              ),
            },
            {
              path: '/verify-email',
              element: <VerifyEmailWrapper />,
            },
            {
              path: '/coachmark-demo',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CoachmarkDemoPage />
                </Suspense>
              ),
            },
            {
              path: '/tutorial-demo',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <TutorialDemoPage />
                </Suspense>
              ),
            },
            {
              path: '/seller/missions/:missionId',
              element: (
                <ProtectedRoute requireAuth>
                  <Suspense fallback={<PageLoader />}>
                    <SellerMissionDetailPage />
                  </Suspense>
                </ProtectedRoute>
              ),
            },

            {
              path: '/unsubscribed',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <UnsubscribedPage />
                </Suspense>
              ),
            },

            {
              // Suivi de colis public : lien depuis l'email
              // SHIPPING_NOTIFICATION acheteur. Aucune auth requise — la
              // securite est dans le token JWT verifie cote backend.
              path: '/track/:orderId',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <TrackOrderPage />
                </Suspense>
              ),
            },

            {
              path: '/forgot-password',
              element: <ForgotPasswordWrapper />,
            },

            {
              path: '/login',
              element: <Navigate to="/" replace />,
            },
            {
              path: '/register',
              element: <RegisterRedirectWrapper />,
            },
            {
              path: '/reset-password',
              element: <ResetPasswordWrapper />,
            },
            {
              path: '/search',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <SearchPage />
                </Suspense>
              ),
            },
            {
              path: '/explorer',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <ExplorerPage onShowLogin={() => setShowLoginModal(true)} />
                </Suspense>
              ),
            },
            {
              // Page d'atterrissage mamans (bio TikTok / Insta / Facebook).
              // /mamans = alias.
              path: '/famille',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <FamilyLandingPage />
                </Suspense>
              ),
            },
            {
              path: '/mamans',
              element: <Navigate to="/famille" replace />,
            },
            {
              // Capsule vacances — V1 du cadre /moments (cf. memoire
              // project_glowp_territoires_familiaux). 'Prepare la valise
              // famille en 30 secondes' : 3 questions -> liste de besoins
              // par enfant -> deeplinks vers /products filtres.
              path: '/vacances',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <VacancesPage />
                </Suspense>
              ),
            },
            {
              // Cadeau maitresse fin d'annee — V2 du cadre /moments.
              // Verticale createurs HANDMADE = differenciateur vs Vinted.
              path: '/cadeau-maitresse',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CadeauMaitressePage />
                </Suspense>
              ),
            },
            {
              // Cadeau naissance — V3 du cadre /moments. Permanent (pas
              // saisonnier comme maitresse). Cible : marraine, grand-
              // parent, ami(e), nouvelle maman.
              path: '/cadeau-naissance',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CadeauNaissancePage />
                </Suspense>
              ),
            },
            {
              // Grandes occasions — V4 du cadre /moments. Mariage,
              // bapteme, communion, photo famille, anniversaire enfant.
              // S'appuie sur ai_occasion (backfill 29/05/2026).
              path: '/grandes-occasions',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <GrandesOccasionsPage />
                </Suspense>
              ),
            },
            {
              // Hub /moments — URL unique a poser en bio TikTok/Instagram.
              // Aiguille vers les 3 entrees du cadre (vacances, maitresse,
              // naissance). Cf. memoire project_glowp_territoires_familiaux.
              path: '/moments',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <MomentsPage />
                </Suspense>
              ),
            },
            {
              path: '/products',
              element: (
                <ProductsPageWrapper
                  onShowLogin={() => setShowLoginModal(true)}
                />
              ),
            },
            {
              path: '/missions',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <MissionsListPage />
                </Suspense>
              ),
            },
            {
              path: '/missions/:missionId',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <MissionResultsPage />
                </Suspense>
              ),
            },
            {
              path: '/product/:productId',
              element: (
                <ProductDetailWrapper
                  onShowLogin={() => setShowLoginModal(true)}
                  onOpenCart={() => setIsCartModalOpen(true)}
                />
              ),
            },
            {
              path: '/product/:productId/edit',
              element: <ProductEditWrapper />,
            },
            {
              path: '/product/:productId/stats',
              element: <ProductStatsWrapper />,
            },
            {
              path: '/product/:productId/duplicate',
              element: <ProductDuplicateWrapper />,
            },
            {
              path: '/seller/:sellerId',
              element: (
                <SellerShopWrapper
                  onShowLogin={() => setShowLoginModal(true)}
                />
              ),
            },
            {
              // Page dédiée d'une collection vendeur. URL partageable
              // (Insta/WhatsApp), SEO, hero avec cover + description.
              // Transforme les collections d'un simple filtre boutique
              // en vraie vitrine éditoriale.
              path: '/seller/:sellerId/collection/:collectionSlug',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CollectionPage />
                </Suspense>
              ),
            },
            {
              path: '/profile/:profileId',
              element: (
                <ProfileDetailsWrapper
                  onShowCreateProduct={() => setShowPublishChoice(true)}
                />
              ),
            },
            {
              path: '/account',
              element: <ProfilePageWrapper />,
            },
            {
              path: '/checkout/:vendorId',
              element: <CheckoutWrapper />,
            },
            {
              path: '/checkout/success',
              element: <CheckoutSuccessWrapper />,
            },
            {
              path: '/checkout/cancel',
              element: <CheckoutCancelWrapper />,
            },
            {
              path: '/admin',
              element: <AdminWrapper />,
            },
            {
              path: '/community',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CommunityFeedPage />
                </Suspense>
              ),
            },
            {
              path: '/messages',
              element: <MessagesWrapper />,
            },
            {
              path: '/messages/:conversationId',
              element: <MessagesWrapper />,
            },
            {
              path: '/subscription',
              element: <SubscriptionWrapper />,
            },
            {
              path: '/subscription/success',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <SubscriptionSuccessPage />
                </Suspense>
              ),
            },
            {
              path: '/pricing',
              element: <PricingPage />,
            },
            {
              path: '/orders',
              element: <OrdersWrapper />,
            },
            {
              path: '/orders/:orderId',
              element: <OrderDetailWrapper />,
            },
            {
              path: '/seller/dashboard',
              element: <SellerDashboardWrapper />,
            },
            {
              path: '/seller/import',
              element: <ImportFromScreenshotsWrapper />,
            },
            {
              path: '/buyer/dashboard',
              element: <BuyerDashboardWrapper />,
            },
            {
              path: '/wallet',
              element: <WalletWrapper />,
            },
            {
              path: '/favorites',
              element: <FavoritesWrapper />,
            },
            {
              path: '/notifications',
              element: <NotificationsWrapper />,
            },
            {
              path: '/disputes',
              element: <DisputesWrapper />,
            },
            {
              path: '/cgu',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <CGUPage />
                </Suspense>
              ),
            },
            {
              path: '/privacy',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPage />
                </Suspense>
              ),
            },
            {
              path: '/politique-confidentialite',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPage />
                </Suspense>
              ),
            },
            {
              path: '/mentions-legales',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <MentionsLegalesPage />
                </Suspense>
              ),
            },
            {
              path: '/suppression-compte',
              element: (
                <Suspense fallback={<PageLoader />}>
                  <DeleteAccountPage />
                </Suspense>
              ),
            },
            // Vitrine du design system — exposée uniquement en
            // développement, jamais dans le build soumis aux stores.
            ...(import.meta.env.DEV
              ? [
                  {
                    path: '/design-system',
                    element: (
                      <Suspense fallback={<PageLoader />}>
                        <DesignSystemPage />
                      </Suspense>
                    ),
                  },
                ]
              : []),
            {
              // Catch-all 404 — toute URL inconnue à l'intérieur du shell.
              // Affiche notre ErrorState branded "pièce disparue du dressing".
              path: '*',
              element: (
                <ErrorState
                  variant="not-found"
                  secondaryAction={{
                    label: 'Voir le catalogue',
                    onClick: () => {
                      glowpNavigate('#/products');
                    },
                  }}
                />
              ),
            },
          ],
        },
      ]),
    [handleSell]
  );

  return (
    <>
      {/* 📢 NOTIFICATION BANNER */}
      {message && (
        <div
          className="fixed top-0 left-0 right-0 z-[100000] px-4 text-center text-sm font-medium"
          style={{
            background: 'linear-gradient(90deg, #f5ede0, #fdf9f4, #f5ede0)',
            borderBottom: '1px solid #e8d9c4',
            color: '#7a5a3a',
            paddingTop: 'calc(env(safe-area-inset-top) + 10px)',
            paddingBottom: '10px',
          }}
          role="alert"
          aria-live="polite"
        >
          {message}
          <button
            onClick={() => setMessage('')}
            className="ml-3 font-bold hover:opacity-70 transition-opacity text-brand-500"
            aria-label={t('buttons.close', 'Fermer')}
          >
            ×
          </button>
        </div>
      )}

      {/* 🏪 WOW MOMENT — après 1ère publication, bottom sheet brand Glowp */}
      {showSellerOnboardingCTA && !showSellerOnboarding && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[100000]"
          style={{
            background: 'linear-gradient(160deg, #2d2a26 0%, #3a3530 100%)',
            borderRadius: '24px 24px 0 0',
            boxShadow: '0 -8px 32px rgba(45,42,38,0.35)',
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)',
            borderTop: '1px solid rgba(200,168,130,0.18)',
          }}
          role="alert"
          aria-live="polite"
        >
          {/* Handle */}
          <div className="flex justify-center pt-3 pb-1">
            <div
              className="w-10 h-1 rounded-full"
              style={{ background: 'rgba(200,168,130,0.35)' }}
            />
          </div>

          <div className="px-5 pt-2 pb-1">
            {/* Header avec apercu boutique */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Avatar / preview */}
                <div
                  className="w-12 h-12 rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center"
                  style={{
                    background:
                      'linear-gradient(135deg, #C8A882 0%, #b8956f 100%)',
                    border: '1px solid rgba(245,235,217,0.25)',
                  }}
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-base font-bold text-white">
                      {(user?.firstName?.[0] || '').toUpperCase()}
                      {(user?.lastName?.[0] || '').toUpperCase()}
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <p
                    className="text-[11px] uppercase tracking-wider font-semibold mb-0.5"
                    style={{ color: '#C8A882' }}
                  >
                    {localStorage.getItem('glowp_seller_onboarding_done') ===
                    'true'
                      ? 'Partage ta nouvelle annonce'
                      : 'Ta boutique est en ligne'}
                  </p>
                  <p className="text-sm font-semibold text-white leading-tight truncate">
                    {user?.firstName
                      ? `Boutique de ${user.firstName}`
                      : 'Ma boutique'}
                  </p>
                  <p
                    className="text-[11px] mt-0.5 truncate"
                    style={{ color: 'rgba(245,235,217,0.55)' }}
                  >
                    glowp.app/s/{user?.id?.slice(0, 8) || ''}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  // Snooze : re-propose dans 5 annonces
                  localStorage.setItem('glowp_seller_cta_snooze', '5');
                  setShowSellerOnboardingCTA(false);
                }}
                className="p-1 rounded-full flex-shrink-0 ml-2 transition-colors"
                style={{ color: 'rgba(245,235,217,0.4)' }}
                aria-label="Fermer"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>

            {/* CTA "Personnaliser" — uniquement si la boutique n'est pas encore
                customisee. Une fois fait (onboarding_done = true), le bottom
                sheet devient un pur outil de partage pour les annonces suivantes. */}
            {localStorage.getItem('glowp_seller_onboarding_done') !==
              'true' && (
              <button
                onClick={() => {
                  setShowSellerOnboardingCTA(false);
                  setShowSellerOnboarding(true);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold mb-2 transition-all hover:opacity-90"
                style={{
                  background:
                    'linear-gradient(135deg, #C8A882 0%, #b8956f 100%)',
                  color: '#2d2a26',
                  boxShadow: '0 4px 16px rgba(200,168,130,0.25)',
                }}
              >
                <Sparkles className="w-4 h-4" />
                Personnaliser ma boutique
              </button>
            )}

            {/* Section partage — driver d'acquisition #1 */}
            {(() => {
              // URL courte qui declenche la route backend /share/:shopId :
              // - Crawlers WhatsApp/Insta lisent les meta tags OG
              // - User reel est redirige vers /#/seller/[shopId] via JS
              // - ?ref=<code> permet l'attribution parrainage (capture
              //   localStorage cote SellerShop, consomme a l'inscription)
              const shopUrl = referralCode
                ? `${window.location.origin}/s/${user?.id}?ref=${encodeURIComponent(referralCode)}`
                : `${window.location.origin}/s/${user?.id}`;
              const shopName = user?.firstName
                ? `Boutique de ${user.firstName}`
                : 'Ma boutique';
              const shareText = `Je viens d'ouvrir ma boutique sur Glowp — mode seconde main et createurs.\nViens decouvrir mes pieces et suis-moi pour ne rien manquer des nouveautes :\n${shopUrl}`;
              const shareTextNoUrl = `Je viens d'ouvrir ma boutique sur Glowp — mode seconde main et createurs. Viens decouvrir mes pieces et suis-moi pour ne rien manquer des nouveautes.`;

              const trackShare = (channel: string) => {
                try {
                  (window as any).gtag?.('event', 'shop_share', {
                    channel,
                    user_id: user?.id,
                  });
                } catch {
                  /* noop */
                }
                if (user?.id) {
                  // Persist en analytics_events pour l'admin (en plus du tracking gtag).
                  void import('./lib/behaviorTracking').then(m =>
                    m.behaviorTracking.shopShared({
                      shopOwnerId: user.id,
                      platform: channel,
                    })
                  );
                }
              };

              const handleNativeShare = async () => {
                if (!navigator.share) return;
                try {
                  await navigator.share({
                    title: shopName,
                    text: shareTextNoUrl,
                    url: shopUrl,
                  });
                  trackShare('native');
                  localStorage.setItem('glowp_seller_cta_snooze', '5');
                } catch {
                  /* User cancelled */
                }
              };

              const openExternal = (url: string, channel: string) => {
                trackShare(channel);
                localStorage.setItem('glowp_seller_cta_snooze', '5');
                window.open(url, '_blank', 'noopener,noreferrer');
              };

              const handleInstagram = async () => {
                try {
                  await navigator.clipboard.writeText(shareText);
                  setShopLinkCopied(true);
                  setTimeout(() => setShopLinkCopied(false), 3500);
                  trackShare('instagram');
                  localStorage.setItem('glowp_seller_cta_snooze', '5');
                } catch {
                  /* clipboard refused */
                }
              };

              const handleCopy = async () => {
                try {
                  await navigator.clipboard.writeText(shopUrl);
                  setShopLinkCopied(true);
                  setTimeout(() => setShopLinkCopied(false), 2500);
                  trackShare('copy');
                } catch {
                  /* noop */
                }
              };

              const canNativeShare =
                typeof navigator !== 'undefined' && 'share' in navigator;

              const platformBtnStyle = {
                background: 'rgba(245,235,217,0.04)',
                border: '1px solid rgba(200,168,130,0.15)',
              };

              return (
                <div>
                  {/* Label section */}
                  <div className="flex items-center gap-2 mb-2.5">
                    <div
                      className="flex-1 h-px"
                      style={{ background: 'rgba(200,168,130,0.15)' }}
                    />
                    <p
                      className="text-[10px] uppercase tracking-[0.15em] font-semibold"
                      style={{ color: 'rgba(245,235,217,0.45)' }}
                    >
                      Fais decouvrir ta boutique
                    </p>
                    <div
                      className="flex-1 h-px"
                      style={{ background: 'rgba(200,168,130,0.15)' }}
                    />
                  </div>

                  {/* Native share (mobile) — full width prominent */}
                  {canNativeShare && (
                    <button
                      onClick={handleNativeShare}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm font-semibold mb-2 transition-all hover:opacity-90"
                      style={{
                        background: 'rgba(200,168,130,0.12)',
                        color: '#C8A882',
                        border: '1px solid rgba(200,168,130,0.35)',
                      }}
                    >
                      <Share2 className="w-4 h-4" />
                      Partager ma boutique
                    </button>
                  )}

                  {/* Platform buttons row */}
                  <div className="grid grid-cols-5 gap-1.5 mb-2">
                    {/* WhatsApp */}
                    <button
                      onClick={() =>
                        openExternal(
                          `https://wa.me/?text=${encodeURIComponent(shareText)}`,
                          'whatsapp'
                        )
                      }
                      className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all hover:opacity-80"
                      style={platformBtnStyle}
                      aria-label="Partager sur WhatsApp"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: '#25D366' }}
                      >
                        <MessageCircle className="w-4 h-4 text-white" />
                      </div>
                      <span
                        className="text-[9px] font-medium"
                        style={{ color: 'rgba(245,235,217,0.65)' }}
                      >
                        WhatsApp
                      </span>
                    </button>

                    {/* Instagram */}
                    <button
                      onClick={handleInstagram}
                      className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all hover:opacity-80"
                      style={platformBtnStyle}
                      aria-label="Partager sur Instagram"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          background:
                            'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                        }}
                      >
                        <Instagram className="w-4 h-4 text-white" />
                      </div>
                      <span
                        className="text-[9px] font-medium"
                        style={{ color: 'rgba(245,235,217,0.65)' }}
                      >
                        Instagram
                      </span>
                    </button>

                    {/* SMS */}
                    <button
                      onClick={() =>
                        openExternal(
                          `sms:?&body=${encodeURIComponent(shareText)}`,
                          'sms'
                        )
                      }
                      className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all hover:opacity-80"
                      style={platformBtnStyle}
                      aria-label="Partager par SMS"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: '#34B7F1' }}
                      >
                        <MessageSquare className="w-4 h-4 text-white" />
                      </div>
                      <span
                        className="text-[9px] font-medium"
                        style={{ color: 'rgba(245,235,217,0.65)' }}
                      >
                        SMS
                      </span>
                    </button>

                    {/* Email */}
                    <button
                      onClick={() =>
                        openExternal(
                          `mailto:?subject=${encodeURIComponent(shopName)}&body=${encodeURIComponent(shareText)}`,
                          'email'
                        )
                      }
                      className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all hover:opacity-80"
                      style={platformBtnStyle}
                      aria-label="Partager par email"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: '#6b5c48' }}
                      >
                        <Mail className="w-4 h-4 text-white" />
                      </div>
                      <span
                        className="text-[9px] font-medium"
                        style={{ color: 'rgba(245,235,217,0.65)' }}
                      >
                        Email
                      </span>
                    </button>

                    {/* Copy link */}
                    <button
                      onClick={handleCopy}
                      className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all hover:opacity-80"
                      style={platformBtnStyle}
                      aria-label="Copier le lien"
                    >
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          background: shopLinkCopied
                            ? '#C8A882'
                            : 'rgba(200,168,130,0.25)',
                        }}
                      >
                        {shopLinkCopied ? (
                          <Check className="w-4 h-4 text-white" />
                        ) : (
                          <Link2
                            className="w-4 h-4"
                            style={{ color: '#C8A882' }}
                          />
                        )}
                      </div>
                      <span
                        className="text-[9px] font-medium"
                        style={{
                          color: shopLinkCopied
                            ? '#C8A882'
                            : 'rgba(245,235,217,0.65)',
                        }}
                      >
                        {shopLinkCopied ? 'Copie' : 'Copier'}
                      </span>
                    </button>
                  </div>

                  {/* Info copy quand Instagram (texte avec lien copie pour coller en bio/story) */}
                  {shopLinkCopied && (
                    <p
                      className="text-[10px] text-center mb-2 leading-tight"
                      style={{ color: 'rgba(200,168,130,0.7)' }}
                    >
                      Lien copie. Colle-le dans ta story ou ta bio Insta.
                    </p>
                  )}

                  {/* Voir ma boutique - tertiary */}
                  <button
                    onClick={() => {
                      localStorage.setItem('glowp_seller_cta_snooze', '5');
                      setShowSellerOnboardingCTA(false);
                      glowpNavigate(`/seller/${user?.id}`);
                    }}
                    className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors"
                    style={{ color: 'rgba(245,235,217,0.5)' }}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Voir ma boutique
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 🚦 ROUTER. La gestion des chunks obsolètes est faite dans
          lazyWithRetry (banner Glowp non bloquante) ; ici on traite
          uniquement les vraies erreurs runtime via ErrorState branded. */}
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <div className="min-h-screen bg-[#F5EFE6]">
            <ErrorState
              variant="crashed"
              technicalDetail={error?.message}
              primaryAction={{
                label: 'Réessayer',
                onClick: resetErrorBoundary,
              }}
            />
          </div>
        )}
        // Forward toute exception qui casse l'arbre React a Sentry pour
        // qu'on ait la stack + le contexte user (deja attache via setSentryUser
        // dans useAuth). Sans ca, l'ErrorBoundary affichait le fallback mais
        // l'erreur disparaissait — invisible aux yeux du dev.
        onError={(error, info) => {
          captureError(error, { componentStack: info.componentStack });
        }}
        onReset={() => window.location.reload()}
      >
        <RouterProvider router={router} />
      </ErrorBoundary>

      {/* 🌟 MODALES GLOBALES */}
      <Suspense fallback={null}>
        {/* LOGIN / REGISTER MODAL — mode piloté par loginModalMode */}
        {showLoginModal && (
          <QuickAuthModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            onSuccess={async () => {
              setShowLoginModal(false);
              // Si l'utilisatrice a exprime une intention avant de s'authentifier
              // (boutons post-register "Ajouter mon premier article" /
              // "Personnaliser ma boutique", ou clic "+" Vendre), on route vers
              // la bonne modal. Sinon : login simple, on garde le toast.
              if (routePostAuthIntent()) return;
              setMessage(t('auth.loginSuccess', 'Connexion réussie !'));
              await getProfile();
            }}
            onRegisterSuccess={email => {
              setShowLoginModal(false);
              setVerificationEmail(email);
              setShowEmailVerificationModal(true);
            }}
            onForgotPassword={() => {
              setShowLoginModal(false);
              glowpNavigate('/forgot-password');
            }}
            defaultMode={loginModalMode}
          />
        )}

        {/* QUICK AUTH MODAL (bouton "+" vendre) */}
        {showQuickAuth && (
          <QuickAuthModal
            isOpen={showQuickAuth}
            defaultMode={quickAuthMode}
            onClose={() => {
              setShowQuickAuth(false);
              localStorage.removeItem('glowp_sell_pending');
            }}
            onSuccess={handleQuickAuthSuccess}
            onRegisterSuccess={email => {
              // Inscription réussie → email obligatoire avant de continuer
              // On garde glowp_sell_pending pour que le formulaire s'ouvre après validation
              localStorage.setItem('glowp_sell_pending', 'true');
              setShowQuickAuth(false);
              setVerificationEmail(email);
              setShowEmailVerificationModal(true);
            }}
            onForgotPassword={() => {
              setShowQuickAuth(false);
              window.location.href = '/forgot-password';
            }}
          />
        )}

        {/* PUBLISH CHOICE MODAL — point d'entrée universel "Vendre / Publier".
            Affiche les 2 options (Une annonce manuelle / Importer en bulk).
            L'option manuelle ouvre SimpleCreateProductModal, l'option bulk
            navigue vers /seller/import. */}
        {showPublishChoice && (
          <Suspense fallback={null}>
            <PublishChoiceModal
              isOpen={showPublishChoice}
              onClose={() => setShowPublishChoice(false)}
              onCreateManual={() => {
                // Pas de gate adresse a la publication : la vendeuse peut
                // publier librement sans donner son adresse. La capture est
                // deplacee a la creation du bordereau (= apres 1ere vente),
                // moment ou la motivation est maximale (7 EUR a recevoir).
                // Cf. doctrine Vinted : zero friction au moment de publier,
                // l'adresse n'est utile que pour expedier. Et grace au
                // backfill Stripe la plupart des vendeurs auront deja leur
                // adresse en BDD silencieusement.
                setShowCreateProduct(true);
              }}
            />
          </Suspense>
        )}

        {/* CREATE PRODUCT MODAL - V4.0 EVENT-DRIVEN */}
        {showCreateProduct && (
          <SimpleCreateProductModal
            isOpen={showCreateProduct}
            onClose={() => setShowCreateProduct(false)}
            onSuccess={handleCreateProductSuccess}
          />
        )}

        {/* La capture d'adresse vendeur ne vit plus ici. Elle a ete
            deplacee sur OrderDetailPage au moment 'Creer mon etiquette'.
            Justification : friction zero a la publication, captation
            uniquement quand la vendeuse a deja une vente a expedier. */}

        {/* SELLER ONBOARDING MODAL — personnalisation boutique après 1ère pub ou CTA "Personnaliser" */}
        {showSellerOnboarding && user && (
          <Suspense fallback={null}>
            <SellerOnboardingModal
              isOpen={showSellerOnboarding}
              userName={user.firstName || ''}
              userId={user.id}
              userAvatarUrl={user.avatarUrl || ''}
              onClose={() => {
                setShowSellerOnboarding(false);
                localStorage.setItem('glowp_seller_onboarding_done', 'true');
              }}
              onCreated={(_shopId, sellerId, action) => {
                setShowSellerOnboarding(false);
                localStorage.setItem('glowp_seller_onboarding_done', 'true');
                // action = 'publish' -> "Publier un article" : on enclenche
                // le flow de creation d article (PublishChoice) au lieu d
                // atterrir sur la boutique. Bug signale par la user :
                // avant les 2 boutons faisaient pareil.
                if (action === 'publish') {
                  setShowPublishChoice(true);
                  return;
                }
                glowpNavigate(`/seller/${sellerId}`);
                // Trigger feedback SHOP_CUSTOMIZED apres la navigation,
                // une fois que la nouvelle page est posee — pour eviter
                // de prompter pendant la transition de route.
                import('./components/feedback/AppFeedbackPrompt').then(
                  ({ hasBeenPrompted, triggerAppFeedback }) => {
                    if (!hasBeenPrompted('SHOP_CUSTOMIZED')) {
                      setTimeout(
                        () => triggerAppFeedback('SHOP_CUSTOMIZED'),
                        2500
                      );
                    }
                  }
                );
              }}
            />
          </Suspense>
        )}

        {/* CART MODAL */}
        <CartModal
          isOpen={isCartModalOpen}
          onClose={() => setIsCartModalOpen(false)}
          onProceedToCheckout={(vendorId: string) => {
            setIsCartModalOpen(false);
            glowpNavigate(`/checkout/${vendorId}`);
          }}
        />
        {/* 🎉 SALE TOAST — notification dopamine vente confirmée */}
        <SaleToast sale={pendingSale} onDismiss={() => setPendingSale(null)} />

        {/* EMAIL VERIFICATION MODAL */}
        {showEmailVerificationModal && (
          <EmailVerificationModal
            email={verificationEmail}
            onClose={() => setShowEmailVerificationModal(false)}
          />
        )}

        {/* WELCOME VERIFIED MODAL — affiché après clic sur lien email (deep link) */}
        {showWelcomeVerified && (
          <WelcomeVerifiedModal
            firstName={welcomeFirstName}
            onClose={() => setShowWelcomeVerified(false)}
          />
        )}

        {/* ONBOARDING MODAL — affiché UNIQUEMENT après vérif email (plan
            validé Marie-Amelie). Avant : la modal s ouvrait des le signup
            (juste apres le success screen QuickAuthModal), ce qui forcait
            la user a remplir parent-info / tailles AVANT d avoir verifie
            son mail. Maintenant : si Explorer d abord -> home en paix,
            la modal n apparait que quand user.isVerified passe a true. */}
        {isAuthenticated &&
          user?.isVerified &&
          needsOnboarding &&
          !onboardingDismissed &&
          !showWelcomeVerified && (
            <OnboardingModal
              isOpen={true}
              userName={user?.firstName || ''}
              parentProfileId={parentProfile?.id}
              onSkip={() => {
                // Re-proposé dans 72h
                const until = Date.now() + 72 * 60 * 60 * 1000;
                localStorage.setItem(
                  'glowp_onboarding_dismissed_until',
                  String(until)
                );
                setOnboardingDismissed(true);
                // Si une vente était en attente, on ouvre la choice modal
                // pour exposer les 2 options (manuel + bulk import).
                if (sellPendingAfterOnboarding) {
                  setSellPendingAfterOnboarding(false);
                  setShowPublishChoice(true);
                }
              }}
              onComplete={() => {
                // Définitivement terminé
                localStorage.setItem(
                  'glowp_onboarding_dismissed_until',
                  String(Number.MAX_SAFE_INTEGER)
                );
                setOnboardingDismissed(true);
                // Remettre le profil parent actif (createProfile sélectionne le dernier enfant créé)
                if (parentProfile?.id) {
                  switchProfile(parentProfile.id);
                }
                // Si une vente était en attente, on ouvre la choice modal
                if (sellPendingAfterOnboarding) {
                  setSellPendingAfterOnboarding(false);
                  setShowPublishChoice(true);
                }
              }}
              onUpdateParent={handleOnboardingUpdateParent}
              onCreateChild={handleOnboardingCreateChild}
              isLoading={isCreatingProfile}
            />
          )}
        {/* Rappel doux : ajouter des profils enfants */}
        {showKidsReminder && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md">
            <div
              className="rounded-2xl p-4 shadow-lg border"
              style={{
                background: 'linear-gradient(135deg, #fffaf5, #fff7f0)',
                borderColor: '#f0e6d3',
                boxShadow: '0 8px 30px rgba(200,168,130,0.2)',
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(200,168,130,0.15)' }}
                >
                  <Baby
                    className="w-5 h-5"
                    style={{ color: 'var(--brand-500)' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-brand-dark">
                    Ajoutez vos enfants
                  </p>
                  <p className="text-xs text-brand-warm mt-0.5 leading-relaxed">
                    Creez leurs profils pour des recommandations personnalisees
                    par taille et age.
                  </p>
                  <button
                    onClick={() => {
                      localStorage.removeItem('glowp_kids_reminder_at');
                      setShowKidsReminder(false);
                      glowpNavigate('#/account?tab=overview');
                    }}
                    className="inline-block mt-2 text-xs font-semibold underline underline-offset-2"
                    style={{ color: 'var(--brand-500)' }}
                  >
                    Aller sur mon profil →
                  </button>
                </div>
                <button
                  onClick={() => {
                    localStorage.removeItem('glowp_kids_reminder_at');
                    setShowKidsReminder(false);
                  }}
                  className="p-1 rounded-lg hover:bg-black/5 transition-colors flex-shrink-0"
                  aria-label="Fermer"
                >
                  <XIcon className="w-4 h-4 text-brand-muted" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Banner RGPD : opt-in marketing differé pour les users inscrits
            avant le fix du 2026-05-16 dont le consentement a ete perdu.
            Le composant auto-gate sur user + acceptedMarketingAt + snooze. */}
        {isAuthenticated && user && <MarketingConsentBanner />}

        {/* Banner discret pour solliciter les users EXISTANTS (inscrits
            avant le launch du systeme feedback). AppFeedbackPrompt est
            monte plus bas (sous AuthProvider via Wrapper). */}
        {isAuthenticated && user && <ExistingUserFeedbackBanner />}
      </Suspense>
    </>
  );
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

// APP WRAPPER PRINCIPAL
function App() {
  const inner = (
    <ToastProvider>
      <AuthProvider>
        <ProfilesProvider>
          <UpsellProvider>
            <AppContent />
            {/* Drop alerts temps réel via WebSocket — likes reçus, suivi,
                offre, vente, drop de boutique suivie. Doit être DANS
                AuthProvider car le composant appelle useAuth(). */}
            <RealtimeNotificationToasts />
            {/* Modal feedback app (FREQUENT_VISIT / FIRST_PUBLISH / etc.).
                Doit être DANS AuthProvider car le composant appelle
                useAuth() — même bug que RealtimeNotificationToasts. */}
            <AppFeedbackPrompt />
            {/* Modal bonus campagne signup (ex: 100 credits IA offerts
                via /famille). Self-mount : lit sessionStorage et n'affiche
                que si un bonus est en attente. No-op sinon. */}
            <SignupBonusWelcome />
          </UpsellProvider>
        </ProfilesProvider>
      </AuthProvider>
      <ToastRenderer />
      <CrispChat />
    </ToastProvider>
  );
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {inner}
      </GoogleOAuthProvider>
    );
  }
  return inner;
}

export default App;
